<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import pipelineAutomationTemplatesAPI from 'dashboard/api/pipelineAutomationTemplates';

const emit = defineEmits(['close', 'templateUsed']);

const { t } = useI18n();

const templates = ref([]);
const categories = ref([]);
const loading = ref(true);
const applyingId = ref(null);
const activeCategory = ref('all');
const searchQuery = ref('');

const categoryLabels = computed(() => ({
  general: t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.GENERAL'),
  sales: t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.SALES'),
  support: t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.SUPPORT'),
  marketing: t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.MARKETING'),
  onboarding: t(
    'PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.ONBOARDING'
  ),
  notifications: t(
    'PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.NOTIFICATIONS'
  ),
  integrations: t(
    'PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.INTEGRATIONS'
  ),
}));

const categoryTabs = computed(() => {
  const tabs = [
    {
      id: 'all',
      label: t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.CATEGORIES.ALL'),
    },
  ];
  categories.value.forEach(cat => {
    tabs.push({
      id: cat,
      label: categoryLabels.value[cat] || cat,
    });
  });
  return tabs;
});

const featuredTemplates = computed(() =>
  templates.value.filter(item => item.is_featured)
);

const filteredTemplates = computed(() => {
  let list = templates.value;

  if (activeCategory.value !== 'all') {
    list = list.filter(item => item.category === activeCategory.value);
  }

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(
      item =>
        item.name.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q))
    );
  }

  return list;
});

const fetchTemplates = async () => {
  loading.value = true;
  try {
    const { data } = await pipelineAutomationTemplatesAPI.list({
      locale: 'pt-BR',
    });
    templates.value = data.templates || [];
    categories.value = data.categories || [];
  } catch {
    templates.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(fetchTemplates);

const applyTemplate = async template => {
  if (applyingId.value) return;
  applyingId.value = template.id;
  try {
    const { data } = await pipelineAutomationTemplatesAPI.use(template.id);
    emit('templateUsed', data);
  } catch {
    // silently handled
  } finally {
    applyingId.value = null;
  }
};

const categoryLabel = cat => {
  return categoryLabels.value[cat] || cat;
};

const handleVisibilityChange = isVisible => {
  if (!isVisible) emit('close');
};
</script>

<template>
  <Modal show size="large" @update:show="handleVisibilityChange">
    <section
      class="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl bg-n-background"
    >
      <!-- Panel Header -->
      <div
        class="flex flex-shrink-0 items-center justify-between border-b border-n-weak px-4 py-5 pr-16 sm:px-8 sm:py-6 sm:pr-16"
      >
        <div>
          <h2 data-modal-title class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.TITLE') }}
          </h2>
          <p class="text-label-small text-n-slate-11 mt-1">
            {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.SUBTITLE') }}
          </p>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center flex-1 py-24"
      >
        <div
          class="w-10 h-10 border-4 border-n-weak border-t-n-brand rounded-full animate-spin"
        />
        <p class="text-label-small text-n-slate-11 mt-4">
          {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.LOADING') }}
        </p>
      </div>

      <!-- Content -->
      <div v-else class="min-h-0 flex-1 overflow-y-auto">
        <!-- Featured Section -->
        <div
          v-if="
            featuredTemplates.length && !searchQuery && activeCategory === 'all'
          "
          class="px-4 pb-2 pt-6 sm:px-8"
        >
          <p
            class="text-xs font-semibold uppercase tracking-wider text-n-slate-9 mb-4"
          >
            {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.FEATURED_LABEL') }}
          </p>
          <div
            class="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            <div
              v-for="tmpl in featuredTemplates"
              :key="`featured-${tmpl.id}`"
              class="group relative flex flex-col rounded-xl border border-n-brand/30 bg-gradient-to-br from-n-brand/5 to-transparent p-5 transition-[border-color,box-shadow] duration-200 hover:border-n-brand/60 hover:shadow-md motion-reduce:transition-none"
            >
              <!-- Destaque badge -->
              <span
                class="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full bg-n-brand/10 text-n-brand"
              >
                {{
                  t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.FEATURED_BADGE')
                }}
              </span>

              <!-- Icon + Name -->
              <div class="flex items-start gap-3 mb-3 pr-14">
                <span
                  class="size-6 flex-shrink-0"
                  :class="[tmpl.icon || 'i-lucide-zap']"
                />
                <div class="min-w-0">
                  <h4
                    class="text-sm font-semibold text-n-slate-12 leading-tight"
                  >
                    {{ tmpl.name }}
                  </h4>
                  <span
                    class="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-10"
                  >
                    {{ categoryLabel(tmpl.category) }}
                  </span>
                </div>
              </div>

              <!-- Description -->
              <p
                v-if="tmpl.description"
                class="text-xs text-n-slate-11 leading-relaxed mb-4 flex-1 line-clamp-2"
              >
                {{ tmpl.description }}
              </p>

              <!-- Footer -->
              <div
                class="flex items-center justify-between mt-auto pt-3 border-t border-n-weak"
              >
                <span class="text-xs text-n-slate-9">
                  {{
                    t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.USAGE_COUNT', {
                      count: tmpl.usage_count || 0,
                    })
                  }}
                </span>
                <Button
                  variant="solid"
                  color="blue"
                  size="sm"
                  :disabled="applyingId === tmpl.id"
                  @click="applyTemplate(tmpl)"
                >
                  <template v-if="applyingId === tmpl.id" #icon>
                    <span
                      class="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"
                    />
                  </template>
                  {{
                    applyingId === tmpl.id
                      ? t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.APPLYING')
                      : t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.USE_SHORT')
                  }}
                </Button>
              </div>
            </div>
          </div>
          <div class="border-b border-n-weak mb-2" />
        </div>

        <!-- Filters (Search + Category Tabs) -->
        <div class="flex-shrink-0 px-4 pb-2 pt-4 sm:px-8">
          <!-- Search -->
          <div class="relative mb-4">
            <FluentIcon
              icon="search"
              size="16"
              :icons="dashboardIcons"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-9 pointer-events-none"
            />
            <input
              v-model="searchQuery"
              type="search"
              autocomplete="off"
              :placeholder="
                t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.SEARCH_PLACEHOLDER')
              "
              :aria-label="
                t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.SEARCH_PLACEHOLDER')
              "
              class="min-h-11 w-full rounded-lg border border-n-weak bg-n-alpha-1 py-2.5 pl-9 pr-4 text-sm text-n-slate-12 transition-colors placeholder:text-n-slate-9 focus:border-n-brand/60 focus:outline-none focus:ring-1 focus:ring-n-brand/30"
            />
          </div>

          <!-- Category Tabs -->
          <div class="flex items-center gap-1 flex-wrap">
            <button
              v-for="tab in categoryTabs"
              :key="tab.id"
              type="button"
              class="min-h-11 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
              :class="
                activeCategory === tab.id
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
              "
              @click="activeCategory = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <!-- Template Grid -->
        <div class="px-4 py-4 sm:px-8">
          <!-- Empty State -->
          <div
            v-if="!filteredTemplates.length"
            class="flex flex-col items-center justify-center py-16 text-center"
          >
            <span class="text-4xl mb-4">
              {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.EMPTY_ICON') }}
            </span>
            <p class="text-sm font-medium text-n-slate-12 mb-1">
              {{ t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.EMPTY_TITLE') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{
                t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.EMPTY_DESCRIPTION')
              }}
            </p>
          </div>

          <!-- Grid -->
          <div
            v-else
            class="grid grid-cols-1 gap-4 pb-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            <div
              v-for="tmpl in filteredTemplates"
              :key="tmpl.id"
              class="group flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-5 transition-[border-color,box-shadow] duration-200 hover:border-n-brand/40 hover:shadow-md motion-reduce:transition-none"
            >
              <!-- Icon + Name -->
              <div class="flex items-start gap-3 mb-3">
                <span
                  class="size-6 flex-shrink-0"
                  :class="[tmpl.icon || 'i-lucide-zap']"
                />
                <div class="min-w-0">
                  <h4
                    class="text-sm font-semibold text-n-slate-12 leading-tight"
                  >
                    {{ tmpl.name }}
                  </h4>
                  <span
                    class="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-10"
                  >
                    {{ categoryLabel(tmpl.category) }}
                  </span>
                </div>
              </div>

              <!-- Description -->
              <p
                v-if="tmpl.description"
                class="text-xs text-n-slate-11 leading-relaxed mb-4 flex-1 line-clamp-3"
              >
                {{ tmpl.description }}
              </p>

              <!-- Footer -->
              <div
                class="flex items-center justify-between mt-auto pt-3 border-t border-n-weak"
              >
                <span class="text-xs text-n-slate-9">
                  {{
                    t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.USAGE_COUNT', {
                      count: tmpl.usage_count || 0,
                    })
                  }}
                </span>
                <Button
                  variant="ghost"
                  color="blue"
                  size="sm"
                  :disabled="applyingId === tmpl.id"
                  @click="applyTemplate(tmpl)"
                >
                  <template v-if="applyingId === tmpl.id" #icon>
                    <span
                      class="inline-block w-3 h-3 border-2 border-n-blue-9/40 border-t-blue-400 rounded-full animate-spin"
                    />
                  </template>
                  {{
                    applyingId === tmpl.id
                      ? t('PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.APPLYING')
                      : t(
                          'PIPELINE.FLOW_BUILDER.TEMPLATES_GALLERY.USE_TEMPLATE'
                        )
                  }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </Modal>
</template>
